import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { JobHttpService } from 'cms/features/feature_job/service';
import { BaseHttpService } from 'cms/core/services';
import { Utils } from 'core/utils';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    MenuItem,
    Alert,
    Box,
    Typography,
    CircularProgress,
    Avatar
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import { HttpService } from 'core/services/http';

function CompanyEditModal({ open, onClose, company, onSaved }) {
    const { register, handleSubmit, formState: { errors }, reset, watch, setValue, control } = useForm();
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const queryClient = useQueryClient();

    const watchedCountryId = watch('country_id');

    useEffect(() => {
        if (open) {
            loadCountries();
            if (company) {
                // Populate form with existing company data
                reset({
                    title: company.title || '',
                    vat_number: company.vat_number || '',
                    number_of_employees: company.number_of_employees || '',
                    first_name: company.manager_name?.split(' ')[0] || '',
                    last_name: company.manager_name?.split(' ').slice(1).join(' ') || '',
                    phone_number: company.phone_number || '',
                    address: company.address || '',
                    country_id: (company.country?.id || company.country_id) ? String(company.country?.id || company.country_id) : '',
                    city: company.city || '',
                    description: company.description || '',
                    email: company.email || ''
                });
                if (company.country?.id || company.country_id) {
                    loadCities(company.country?.id || company.country_id);
                }
                // Set image preview if company has image
                if (company.image) {
                    if (company.image.startsWith('http')) {
                        setImagePreview(company.image);
                    } else if (company.image.startsWith('/')) {
                        setImagePreview(HttpService.SiteBase + company.image);
                    } else {
                        setImagePreview(HttpService.SiteBase + '/' + company.image);
                    }
                } else {
                    setImagePreview(null);
                }
            } else {
                // Reset form for new company
                reset({
                    title: '',
                    vat_number: '',
                    number_of_employees: '',
                    first_name: '',
                    last_name: '',
                    phone_number: '',
                    address: '',
                    country_id: '',
                    city: '',
                    description: '',
                    email: ''
                });
                setImagePreview(null);
            }
            setImageFile(null);
            setError('');
        }
    }, [open, company, reset]);

    useEffect(() => {
        if (watchedCountryId) {
            loadCities(watchedCountryId);
            setValue('city', ''); // Reset city when country changes
        } else {
            setCities([]);
        }
    }, [watchedCountryId, setValue]);

    const loadCountries = async () => {
        try {
            const response = await BaseHttpService.loadCountries();
            if (Array.isArray(response)) {
                setCountries(response);
            } else if (response?.results && Array.isArray(response.results)) {
                setCountries(response.results);
            } else if (response?.data && Array.isArray(response.data)) {
                setCountries(response.data);
            }
        } catch (error) {
            console.error('Error loading countries:', error);
        }
    };

    const loadCities = async (countryId) => {
        try {
            const response = await BaseHttpService.loadCities(countryId);
            if (Array.isArray(response)) {
                setCities(response);
            } else if (response?.results && Array.isArray(response.results)) {
                setCities(response.results);
            } else if (response?.data && Array.isArray(response.data)) {
                setCities(response.data);
            } else {
                setCities([]);
            }
        } catch (error) {
            console.error('Error loading cities:', error);
            setCities([]);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file (JPG, PNG, etc.)');
            e.target.value = ''; // Reset input
            return;
        }
        
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('Image size must be less than 5MB');
            e.target.value = ''; // Reset input
            return;
        }
        
        setImageFile(file);
        setError('');
        
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.onerror = () => {
            setError('Failed to read image file');
            setImageFile(null);
            setImagePreview(null);
        };
        reader.readAsDataURL(file);
    };

    const onSubmit = async (data) => {
        setLoading(true);
        setError('');

        try {
            // Combine first_name and last_name into manager_name for backend
            const submitData = {
                ...data,
                manager_name: `${data.first_name || ''} ${data.last_name || ''}`.trim()
            };
            delete submitData.first_name;
            delete submitData.last_name;

            // Convert country_id to number if it's a string
            if (submitData.country_id && typeof submitData.country_id === 'string') {
                submitData.country_id = submitData.country_id === '' ? null : Number(submitData.country_id);
            }


            let response;
            const service = new HttpService();
            
            // If there's an image file, use FormData
            if (imageFile) {
                const formData = new FormData();
                Object.keys(submitData).forEach(key => {
                    const value = submitData[key];
                    if (value !== null && value !== undefined && value !== '') {
                        // Convert to string if needed (for numbers, booleans, etc.)
                        if (typeof value === 'object' && !(value instanceof File)) {
                            formData.append(key, JSON.stringify(value));
                        } else {
                            formData.append(key, String(value));
                        }
                    }
                });
                formData.append('image', imageFile, imageFile.name);
                
                // Log FormData contents
                
                if (company?.id) {
                    // Update existing company with image - use PATCH for partial update
                    response = await service.patch(`/job/company/${company.id}`, formData, true);
                    console.log(response);
                    console.log("updated company");
                } else {
                    // Create new company with image
                    response = await service.post('/job/company/', formData, true);
                }
            } else {
                // No image file, use regular JSON
                if (company?.id) {
                    response = await JobHttpService.updateCompany(company.id, submitData);
                } else {
                    response = await JobHttpService.createCompany(submitData);
                }
            }

            // Check for errors in response
            if (response) {
                if (response.detail || response.error) {
                    setError(response.detail || response.error || 'Failed to save company');
                } else if (response.id || response.title) {
                    // Success - response has company data
                    queryClient.invalidateQueries('myCompany');
                    if (onSaved) {
                        onSaved();
                    }
                } else {
                    setError('Unexpected response from server');
                }
            } else {
                setError('No response from server');
            }
        } catch (err) {
            console.error('Error saving company:', err);
            setError(err?.message || 'Failed to save company. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle>
                    <Typography variant="h6" fontWeight={600}>
                        {company?.id ? 'Edit Company' : 'Create Company'}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {/* Company Image Upload */}
                        <Grid item xs={12}>
                            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <input
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    id="company-image-upload"
                                    type="file"
                                    onChange={handleImageChange}
                                />
                                <label htmlFor="company-image-upload" style={{ cursor: 'pointer' }}>
                                    {imagePreview ? (
                                        <Avatar
                                            src={imagePreview}
                                            alt="Company"
                                            sx={{ 
                                                width: 150, 
                                                height: 150,
                                                cursor: 'pointer',
                                                border: '2px solid',
                                                borderColor: 'divider',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    opacity: 0.8
                                                },
                                                transition: 'all 0.2s'
                                            }}
                                            variant="rounded"
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                width: 150,
                                                height: 150,
                                                border: '2px dashed',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: 'grey.50',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    bgcolor: 'grey.100'
                                                },
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <ImageIcon sx={{ fontSize: 50, color: 'text.secondary', mb: 1 }} />
                                            <Typography variant="caption" color="text.secondary">
                                                Click to upload
                                            </Typography>
                                        </Box>
                                    )}
                                </label>
                                {imageFile && (
                                    <Typography variant="caption" color="text.secondary">
                                        Selected: {imageFile.name}
                                    </Typography>
                                )}
                                {!imageFile && imagePreview && (
                                    <Typography variant="caption" color="text.secondary">
                                        Click image to change
                                    </Typography>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Company Name *"
                                {...register("title", { required: "Company name is required" })}
                                error={Boolean(errors.title)}
                                helperText={errors.title?.message}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="VAT Number"
                                {...register("vat_number")}
                                error={Boolean(errors.vat_number)}
                                helperText={errors.vat_number?.message}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Number of Employees"
                                {...register("number_of_employees")}
                                error={Boolean(errors.number_of_employees)}
                                helperText={errors.number_of_employees?.message}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="First Name *"
                                {...register("first_name", { required: "First name is required" })}
                                error={Boolean(errors.first_name)}
                                helperText={errors.first_name?.message}
                                placeholder="Your first name"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Last Name *"
                                {...register("last_name", { required: "Last name is required" })}
                                error={Boolean(errors.last_name)}
                                helperText={errors.last_name?.message}
                                placeholder="Your last name"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Phone Number"
                                {...register("phone_number")}
                                error={Boolean(errors.phone_number)}
                                helperText={errors.phone_number?.message}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                {...register("email", {
                                    pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                        message: "Invalid email address"
                                    }
                                })}
                                error={Boolean(errors.email)}
                                helperText={errors.email?.message}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address"
                                {...register("address")}
                                error={Boolean(errors.address)}
                                helperText={errors.address?.message}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="country_id"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        select
                                        label="Country"
                                        value={field.value ?? ''}
                                        error={Boolean(errors.country_id)}
                                        helperText={errors.country_id?.message}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {countries.map((country) => {
                                            const countryName = Utils.getDefaultName(country);
                                            const displayName = countryName && !countryName.startsWith('#') 
                                                ? countryName 
                                                : (country.code || country.name || `Country ${country.id}`);
                                            return (
                                                <MenuItem key={country.id} value={String(country.id)}>
                                                    {displayName}
                                                </MenuItem>
                                            );
                                        })}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="city"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        select
                                        label="City"
                                        value={field.value ?? ''}
                                        error={Boolean(errors.city)}
                                        helperText={errors.city?.message}
                                        disabled={!watchedCountryId}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {cities.map((city) => {
                                            const cityName = Utils.getDefaultName(city);
                                            const displayName = cityName && !cityName.startsWith('#') 
                                                ? cityName 
                                                : (city.name || `City ${city.id}`);
                                            return (
                                                <MenuItem key={city.id} value={displayName}>
                                                    {displayName}
                                                </MenuItem>
                                            );
                                        })}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Company Description"
                                multiline
                                rows={4}
                                {...register("description")}
                                error={Boolean(errors.description)}
                                helperText={errors.description?.message}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button 
                        type="submit" 
                        variant="contained" 
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        {loading ? 'Saving...' : (company?.id ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default CompanyEditModal;

